import TableTooltip from '@/components/common/tooltip/table-tooltip/TableTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import useGetPaginatedSales from '@/hooks/queries/sales-api/useGetPaginatedSales';
import useFormattedDate from '@/hooks/useFormattedDate';
import usePersistedPaginationFiltersAndSorting from '@/hooks/usePersistedPaginationFiltersAndSorting';
import { ServiceSold } from '@/pages/protected/Sales/components/services-sold-table/services-sold-tables.types';
import { statusVariant } from '@/utils/sales';
import { dateInDataRange, fullName } from '@/utils/utils';
import {
  ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table';
import { addMonths, subMonths } from 'date-fns';
import { Eye } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const useServicesSoldTable = () => {
  const { formattedDate } = useFormattedDate();
  const { t } = useTranslation();
  const today = new Date();
  const oneMonthBeforeTimestamp = subMonths(today, 1).getTime();
  const oneMonthAfterTimestamp = addMonths(today, 1).getTime();

  const {
    pagination,
    setPagination,
    filtering,
    setFiltering,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters
  } = usePersistedPaginationFiltersAndSorting({
    defaultSorting: [{ id: 'createdAt', desc: true }],
    defaultColumnFilters: [
      {
        id: 'createdAt',
        value: {
          from: oneMonthBeforeTimestamp,
          to: oneMonthAfterTimestamp
        }
      }
    ]
  });

  const { data, isLoading, isError, isFetching } = useGetPaginatedSales({
    pagination,
    filtering,
    sorting,
    columnFilters
  });

  const sales = data?.results;

  const columns = useMemo<ColumnDef<ServiceSold>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('tables.common.id'),
        cell: ({ row }) => {
          const id = row?.original?.id;
          return <TableTooltip text={id} />;
        }
      },
      {
        id: 'user.firstName',
        accessorKey: 'user.firstName',
        accessorFn: (originalRow) =>
          originalRow?.user?.firstName && originalRow?.user?.lastName
            ? fullName({
                firstName: originalRow.user.firstName,
                lastName: originalRow.user.lastName
              })
            : ' - ',
        header: t('common.user')
      },
      {
        id: 'participants',
        accessorKey: 'participants',
        accessorFn: (originalRow) => {
          const participants = originalRow.participants;
          const participantsName = participants.map((participant) =>
            fullName({
              firstName: participant.firstName,
              lastName: participant.lastName
            })
          );
          return participantsName.join(', ');
        },
        enableSorting: false,
        header: t('tables.common.participants')
      },
      {
        id: 'saleItems.service.id',
        accessorKey: 'service.name',
        header: t('tables.common.activity'),
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        }
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        accessorFn: (originalRow) =>
          formattedDate({ date: originalRow.createdAt }),
        header: t('tables.common.date'),
        filterFn: (row, id, value) =>
          dateInDataRange(new Date(row.getValue(id)), value)
      },
      {
        id: 'status',
        accessorKey: 'status',
        accessorFn: (originalRow) => {
          const status = originalRow.status.name;
          return t(`tables.servicesSold.${status}`);
        },
        header: t('tables.common.status'),
        cell: ({ row }) => {
          const status = row.original.status.name;

          return (
            <Badge variant={statusVariant[status]}>
              {t(`tables.servicesSold.${status}`)}
            </Badge>
          );
        }
      },
      {
        id: 'actions',
        header: t('tables.common.actions'),
        cell: ({ row }) => {
          const serviceSold = row.original;
          return (
            <Button asChild variant="outline" size="icon">
              <Link to={`/sales/${serviceSold.id}`}>
                <Eye />
              </Link>
            </Button>
          );
        },
        enableHiding: false
      }
    ],
    [formattedDate, t]
  );

  const table = useReactTable({
    data: sales ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    //pagination config
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    rowCount: data?.total,
    pageCount: Math.ceil((data?.total ?? 0) / (data?.limit ?? 1)),
    manualPagination: true, //turn off client-side pagination
    //sort config
    onSortingChange: setSorting,
    enableMultiSort: false,
    manualSorting: true,
    sortDescFirst: true,
    //filter configuration
    onColumnFiltersChange: setColumnFilters,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    initialState: {
      columnVisibility: {
        id: false
      }
    },
    state: {
      columnFilters,
      pagination,
      sorting
    }
  });
  return { isError, isLoading, isFetching, table, filtering, setFiltering };
};

export default useServicesSoldTable;
